/**
 * v3.21-1762 · Deterministic offer ranking + grouping helpers.
 *
 * Pure functions. No React, no side effects, no randomness. Every derived
 * record retains a stable `originalIndex` pointing at the caller-supplied
 * `offers[]` array so the existing shortlist selection state
 * (`selectedIndices: number[]`) continues to be the SINGLE source of truth
 * for "which offers are selected" — whether the click came from a
 * Recommendation card OR from an Approved-Amount group row, they resolve
 * to the same underlying offer object and the same index.
 *
 * Ranking rules (equal weight on the three Client priorities):
 *   PAYMENT RANK  — lowest monthly payment  = rank 1
 *   TERM RANK     — longest term            = rank 1
 *   APR RANK      — lowest APR              = rank 1
 *   overall_rank_score = payment_rank + term_rank + apr_rank
 *   LOWEST total score wins.
 * Ties (in order): lower payment → longer term → lower APR
 *                → higher approved amount → stable original index.
 *
 * Qualifying pool for recommendations:
 *   PRIMARY   — offers whose approved_amount >= requested (fully covers).
 *   FALLBACK  — if primary is empty, use ALL offers matching the HIGHEST
 *               approved amount available. Prevents a tiny underfunded
 *               approval from winning purely on a low absolute payment.
 */

export const num = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(String(v).replace(/[$,\s%]/g, '')) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const normalizeOffer = (offer, index) => ({
  raw: offer,
  originalIndex: index,
  approved: num(offer?.approved_amount ?? offer?.approvedAmount ?? offer?.amount),
  term:     num(offer?.term            ?? offer?.months),
  apr:      num(offer?.apr             ?? offer?.rate),
  payment:  num(offer?.monthly_payment ?? offer?.payment),
  offerId:  offer?.offer_id ?? offer?.offerId ?? null,
});

/**
 * Primary vs fallback qualifying-pool selection.
 * Returned `mode` documents the deterministic branch taken.
 */
export const buildQualifyingPool = (normalized, requestedAmount) => {
  const req = num(requestedAmount);
  if (req > 0) {
    const primary = normalized.filter(o => o.approved > 0 && o.approved >= req);
    if (primary.length > 0) return { pool: primary, mode: 'covers_request' };
  }
  const nonZero = normalized.filter(o => o.approved > 0);
  if (nonZero.length === 0) return { pool: [], mode: 'no_meaningful_offers' };
  const max = Math.max(...nonZero.map(o => o.approved));
  return { pool: nonZero.filter(o => o.approved === max), mode: 'highest_tier_fallback' };
};

// Rank map keyed by `originalIndex`. Missing/zero metric → worst rank.
const rankBy = (pool, valueFn, direction /* 'asc' | 'desc' */) => {
  const sorted = [...pool].sort((a, b) => {
    const va = valueFn(a);
    const vb = valueFn(b);
    const aMissing = !(va > 0);
    const bMissing = !(vb > 0);
    if (aMissing && !bMissing) return 1;
    if (bMissing && !aMissing) return -1;
    if (aMissing && bMissing)  return a.originalIndex - b.originalIndex;
    return direction === 'asc' ? va - vb : vb - va;
  });
  const map = new Map();
  sorted.forEach((o, i) => map.set(o.originalIndex, i + 1));
  return map;
};

export const scorePool = (pool) => {
  const paymentRanks = rankBy(pool, o => o.payment, 'asc');
  const termRanks    = rankBy(pool, o => o.term,    'desc');
  const aprRanks     = rankBy(pool, o => o.apr,     'asc');
  return pool.map(o => ({
    ...o,
    paymentRank: paymentRanks.get(o.originalIndex),
    termRank:    termRanks.get(o.originalIndex),
    aprRank:     aprRanks.get(o.originalIndex),
    overallRank: paymentRanks.get(o.originalIndex)
               + termRanks.get(o.originalIndex)
               + aprRanks.get(o.originalIndex),
  }));
};

const compareRecommended = (a, b) => {
  if (a.overallRank !== b.overallRank) return a.overallRank - b.overallRank; // lower total = better
  if (a.payment    !== b.payment)      return a.payment - b.payment;         // lower payment
  if (a.term       !== b.term)         return b.term - a.term;               // longer term
  if (a.apr        !== b.apr)          return a.apr - b.apr;                 // lower APR
  if (a.approved   !== b.approved)     return b.approved - a.approved;       // higher approved
  return a.originalIndex - b.originalIndex;                                  // stable
};

/**
 * v3.21-1838 · ORIGINATION-FEE-AWARE TOP-TWO (single engine for Deal Dashboard,
 * Lender Offers and the Financing Result message).
 *
 * The lender's origination fee is disclosed only in the client's paperwork and
 * is never read from offers, persisted, or displayed.  A conservative 9.99% is
 * used ONLY as ranking math to size the loan:
 *   feeProtectedMinimum = requested / (1 - 0.0999)
 *   preferredMinimum    = max(requested + 1000, feeProtectedMinimum)
 *   preferredMaximum    = max(requested + 1500, preferredMinimum)
 * Offers below feeProtectedMinimum are never recommended.  Fee-protected offers
 * are bucketed by distance from the preferred range ($500 buckets; 0 = ideal),
 * so a small APR edge never beats thousands of unnecessary principal.  Within a
 * bucket: lower APR → lower total cost → lower payment → longer term → index.
 */
export const ASSUMED_FEE_RATE = 0.0999;
export const EXCESS_BUCKET = 500;

export const sizingTargets = (requestedAmount) => {
  const req = num(requestedAmount);
  const feeProtectedMinimum = req / (1 - ASSUMED_FEE_RATE);
  const preferredMinimum = Math.max(req + 1000, feeProtectedMinimum);
  const preferredMaximum = Math.max(req + 1500, preferredMinimum);
  return { requested: req, feeProtectedMinimum, preferredMinimum, preferredMaximum };
};

const totalCost = (o) => (o.payment > 0 && o.term > 0 ? o.payment * o.term - o.approved : Infinity);
const orMax = (v) => (v > 0 ? v : Infinity);

const compareSized = (a, b) => {
  if (a.excessBucket !== b.excessBucket) return a.excessBucket - b.excessBucket;
  if (orMax(a.apr) !== orMax(b.apr)) return orMax(a.apr) - orMax(b.apr);
  if (totalCost(a) !== totalCost(b)) return totalCost(a) - totalCost(b);
  if (orMax(a.payment) !== orMax(b.payment)) return orMax(a.payment) - orMax(b.payment);
  if (a.term !== b.term) return b.term - a.term;
  return a.originalIndex - b.originalIndex;
};

export const rankFeeAwareOffers = (offers, requestedAmount) => {
  const t = sizingTargets(requestedAmount);
  return (offers || []).map(normalizeOffer)
    .filter((o) => o.approved > 0 && o.approved >= t.feeProtectedMinimum)
    .map((o) => {
      const distance = o.approved < t.preferredMinimum ? t.preferredMinimum - o.approved
        : o.approved > t.preferredMaximum ? o.approved - t.preferredMaximum : 0;
      const excessBucket = distance === 0 ? 0 : Math.ceil(distance / EXCESS_BUCKET);
      return { ...o, excessBucket, tier: excessBucket === 0 ? 1 : 2 };
    })
    .sort(compareSized);
};

export const pickTopTwoRecommendations = (offers, requestedAmount) => {
  if (!Array.isArray(offers) || offers.length === 0) return { top: [], mode: 'empty' };
  if (!(num(requestedAmount) > 0)) {
    // No Applied For amount to size against → legacy highest-approval fallback.
    const { pool, mode } = buildQualifyingPool(offers.map(normalizeOffer), 0);
    return { top: scorePool(pool).sort(compareRecommended).slice(0, 2), mode };
  }
  const ranked = rankFeeAwareOffers(offers, requestedAmount);
  if (ranked.length === 0) return { top: [], mode: 'no_fee_protected_offers' };
  return { top: ranked.slice(0, 2), mode: ranked[0].tier === 1 ? 'fee_protected_ideal' : 'fee_protected_nearest' };
};

// v3.21-1838 · ONE requested-amount resolver for every Top-Two caller (Applied For first).
export const resolveRequestedAmount = (client) =>
  num(client?.loan_amount_requested) || num(client?.requested_amount) || num(client?.deal_amount);

/**
 * Group offers by exact numeric approved_amount.
 * Groups sorted HIGHEST → LOWEST.
 * Within a group: longest term → lowest payment → lowest APR → original index.
 * Each derived record carries `.raw` (original offer object) and
 * `.originalIndex` (position in the caller's offers[]) so selection
 * remains SSOT-safe.
 */
export const groupOffersByApprovedAmount = (offers) => {
  if (!Array.isArray(offers) || offers.length === 0) return [];
  const normalized = offers.map(normalizeOffer);
  const groups = new Map();
  for (const o of normalized) {
    if (!groups.has(o.approved)) groups.set(o.approved, []);
    groups.get(o.approved).push(o);
  }
  const sortedKeys = [...groups.keys()].sort((a, b) => b - a);
  return sortedKeys.map((amount) => {
    const items = groups.get(amount).sort((a, b) => {
      if (a.term    !== b.term)    return b.term - a.term;
      if (a.payment !== b.payment) return a.payment - b.payment;
      if (a.apr     !== b.apr)     return a.apr - b.apr;
      return a.originalIndex - b.originalIndex;
    });
    const terms    = items.map(i => i.term).filter(v => v > 0);
    const aprs     = items.map(i => i.apr).filter(v => v > 0);
    const payments = items.map(i => i.payment).filter(v => v > 0);
    return {
      approvedAmount: amount,
      count:          items.length,
      termRange:      terms.length    ? [Math.min(...terms),    Math.max(...terms)]    : null,
      aprRange:       aprs.length     ? [Math.min(...aprs),     Math.max(...aprs)]     : null,
      paymentRange:   payments.length ? [Math.min(...payments), Math.max(...payments)] : null,
      offers:         items,
    };
  });
};
